import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProductForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(isEditMode);
  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: '',
    discount: '0',
    featured: false,
    image: null
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }

    fetchCategories();
    if (isEditMode) {
      fetchProduct();
    }
  }, [navigate, isEditMode, id]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error carregant categories:', error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      const product = response.data;
      setFormData({
        name: product.name,
        price: product.price.toString(),
        category: product.category,
        discount: product.discount.toString(),
        featured: product.featured,
        image: null
      });
      setImagePreview(product.image_url);
    } catch (error) {
      console.error('Error carregant producte:', error);
      toast.error('Error carregant producte');
    } finally {
      setLoadingProduct(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setFormData({ ...formData, image: null });
    setImagePreview(isEditMode ? null : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isEditMode && !formData.image) {
      toast.error('Si us plau, selecciona una imatge');
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('price', parseFloat(formData.price));
      data.append('category', formData.category);
      data.append('discount', parseFloat(formData.discount));
      data.append('featured', formData.featured);
      
      if (formData.image) {
        data.append('image', formData.image);
      }

      if (isEditMode) {
        await axios.put(`${API}/products/${id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Producte actualitzat');
      } else {
        await axios.post(`${API}/products`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Producte creat');
      }

      navigate('/admin/products');
    } catch (error) {
      console.error('Error desant producte:', error);
      toast.error('Error desant producte');
    } finally {
      setLoading(false);
    }
  };

  if (loadingProduct) {
    return (
      <div className="min-h-screen bg-muted/30 px-6 py-12 flex items-center justify-center">
        <p className="text-muted-foreground">Carregant producte...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-product-form" className="min-h-screen bg-muted/30 px-6 py-12">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <Link to="/admin/products" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" />
            Tornar als productes
          </Link>
          <h1 className="font-heading text-4xl font-medium text-foreground">
            {isEditMode ? 'Editar Producte' : 'Nou Producte'}
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-card rounded-2xl shadow-sm border border-border/50 p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload */}
            <div>
              <Label className="text-lg mb-3 block">Imatge</Label>
              {imagePreview ? (
                <div className="relative aspect-square max-w-xs rounded-2xl overflow-hidden bg-muted">
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-contain" />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={clearImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="image-upload"
                  data-testid="image-upload-zone"
                  className="aspect-square max-w-xs rounded-3xl border-2 border-dashed border-primary/30 bg-primary/5 flex flex-col items-center justify-center text-center p-8 cursor-pointer hover:bg-primary/10 transition-colors"
                >
                  <Upload className="h-12 w-12 text-primary mb-4" />
                  <p className="text-lg font-medium text-foreground mb-2">Puja una imatge</p>
                  <p className="text-sm text-muted-foreground">PNG, JPG fins a 10MB</p>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    data-testid="image-input"
                  />
                </label>
              )}
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-lg mb-2">Nom del producte</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="product-name"
                className="h-14 rounded-xl text-lg"
                required
              />
            </div>

            {/* Price */}
            <div>
              <Label htmlFor="price" className="text-lg mb-2">Preu (€)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                data-testid="product-price"
                className="h-14 rounded-xl text-lg"
                required
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="category" className="text-lg mb-2">Categoria</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
                required
              >
                <SelectTrigger data-testid="product-category" className="h-14 rounded-xl text-lg">
                  <SelectValue placeholder="Selecciona una categoria" />
                </SelectTrigger>
                <SelectContent data-testid="category-dropdown-content">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id} data-testid={`category-option-${cat.id}`}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Discount */}
            <div>
              <Label htmlFor="discount" className="text-lg mb-2">Descompte (%)</Label>
              <Input
                id="discount"
                type="number"
                step="1"
                min="0"
                max="100"
                value={formData.discount}
                onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                data-testid="product-discount"
                className="h-14 rounded-xl text-lg"
              />
            </div>

            {/* Featured */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                data-testid="product-featured"
                className="h-5 w-5 rounded"
              />
              <Label htmlFor="featured" className="text-lg cursor-pointer">
                Producte destacat
              </Label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-product"
              className="w-full h-14 text-lg font-semibold rounded-xl"
            >
              {loading ? 'Desant...' : isEditMode ? 'Actualitzar Producte' : 'Crear Producte'}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminProductForm;